import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EntityDrawer,
  PageHeader,
  StatutoryDefaultsCallout,
} from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { LoanProductDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// Loan Products catalog (M16.7). Filterable list + per-row inline edit
// + deactivate. Auto-seeded with PERSONAL / EMERGENCY / HOUSING on
// every new LegalEntity via the M7 onboarding hook.

function NewLoanProductDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    description: "",
    max_amount: "50000",
    max_tenure_months: 24,
    min_service_months_eligibility: 0,
    max_concurrent_loans_per_employee: 1,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.createLoanProduct({
        legal_entity_id: legalEntityId,
        ...form,
      }),
    onSuccess: () => onCreated(),
    onError: (err) =>
      setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.loan_products_new")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-loan-product-form"
            disabled={m.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {m.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-loan-product-form"
          onSubmit={(e) => {
            e.preventDefault();
            setErrorMsg(null);
            m.mutate();
          }}
          className="flex flex-col gap-3 text-sm"
        >
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.code_label")}
              </span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.loan_products_currency_max")}
              </span>
              <input
                value={form.max_amount}
                onChange={(e) =>
                  setForm({ ...form, max_amount: e.target.value })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.name_en_label")}
              </span>
              <input
                value={form.name_en}
                onChange={(e) =>
                  setForm({ ...form, name_en: e.target.value })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.name_ar_label")}
              </span>
              <input
                value={form.name_ar}
                onChange={(e) =>
                  setForm({ ...form, name_ar: e.target.value })
                }
                dir="rtl"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.loan_products_max_tenure")}
              </span>
              <input
                type="number"
                min={1}
                max={360}
                value={form.max_tenure_months}
                onChange={(e) =>
                  setForm({
                    ...form,
                    max_tenure_months: parseInt(e.target.value, 10),
                  })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-slate-500">
                {t("policies.loan_products_min_service")}
              </span>
              <input
                type="number"
                min={0}
                value={form.min_service_months_eligibility}
                onChange={(e) =>
                  setForm({
                    ...form,
                    min_service_months_eligibility: parseInt(e.target.value, 10),
                  })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
          </div>
          <label>
            <span className="text-xs font-medium uppercase text-slate-500">
              {t("policies.description_label")}
            </span>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={2}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            />
          </label>
          {errorMsg && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
          )}
        </form>
    </EntityDrawer>
  );
}

function ProductRow({
  product,
  onChanged,
}: {
  product: LoanProductDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const deactivateM = useMutation({
    mutationFn: () =>
      api.updateLoanProduct(product.id, { is_active: false }),
    onSuccess: onChanged,
  });
  const deleteM = useMutation({
    mutationFn: () => api.deleteLoanProduct(product.id),
    onSuccess: onChanged,
  });
  return (
    <article className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <Banknote size={20} className="text-emerald-600" />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/policies/loan-products/${product.id}`}
            className="font-semibold hover:underline"
          >
            {product.name_en}
          </Link>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono">
            {product.code}
          </span>
          {!product.is_active && (
            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
              {t("policies.loan_products_inactive_pill")}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {t("policies.loan_products_row_summary", {
            amount: product.max_amount,
            currency: product.currency,
            tenure: product.max_tenure_months,
            service: product.min_service_months_eligibility,
          })}
        </div>
      </div>
      {product.is_active ? (
        <button
          type="button"
          onClick={() => deactivateM.mutate()}
          disabled={deactivateM.isPending}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
        >
          {t("policies.allowances_deactivate")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => deleteM.mutate()}
          disabled={deleteM.isPending}
          className="rounded border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
        >
          {t("common.delete")}
        </button>
      )}
    </article>
  );
}

export function LoanProductsPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const [showNew, setShowNew] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["loan-products", le, includeInactive],
    queryFn: () => api.listLoanProducts(le, includeInactive),
    enabled: !!le,
  });
  if (gate) return gate;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["loan-products", le] });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policies.loan_products_title")}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("policies.loan_products_title") },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("common.show_inactive")}
            </label>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover"
            >
              <Plus size={14} /> {t("common.new")}
            </button>
          </div>
        }
      />
      <StatutoryDefaultsCallout domain="loan" />
      <div className="flex flex-col gap-2">
        {(q.data ?? []).map((p) => (
          <ProductRow key={p.id} product={p} onChanged={refresh} />
        ))}
      </div>
      {showNew && (
        <NewLoanProductDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
